import { Box, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { getRowsByIds } from "@entrilia/shared/utilities/dataGridApiHelper";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import { fileToUploadValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../api/adminApi";
import type { FileInfo, GridColumnDefinition, SearchFilesRequest } from "../../../api/types/fileTypes";
import DeleteFilesDialog from "./DeleteFilesDialog";
import type { EntityInfo } from "./FilesContext";
import { FilesContextProvider } from "./FilesContextProvider";
import FileDetailsPanel from "./fileDetails/FileDetailsPanel";
import FilesGrid from "./filesGrid/FilesGrid";
import type { FilesRowModel } from "./filesState";
import {
  clearCompletedUploadsAction,
  getInitialFilesState,
  searchFilesAction,
  startUploadingFilesAction,
  updateFileUploadStateAction,
} from "./filesState";
import type { UploadingFile } from "./uploadHelper";
import { getUploadingFiles } from "./uploadHelper";

interface Props {
  viewName?: string;
  tagId?: string;
  viewTitle: string;
  columnDefinitions: GridColumnDefinition[];
  hasPermissionsToManage: boolean;
  uploadParams?: UploadParams;
  entityInfo?: EntityInfo;
}

interface UploadParams {
  acceptedFileExtensions: string[];
  maxFileSize: number;
  dropAreaSubtitle: string;
  uploadFileApiCall: (file: File) => Promise<ApiResponse<FileInfo>>;
}

interface DialogState {
  openDialog?: "preview" | "delete";
  previewFileInfo?: FileDownloadInfo;
  deletingFileIds?: string[];
  deletingFilesDescription?: string;
}

const getFileDownloadInfo = withErrorHandling(adminApi.getFileDownloadInfo);
const getEntityFileDownloadInfo = withErrorHandling(adminApi.getEntityFileDownloadInfo);
const getDownloadUrlForMultipleFiles = withErrorHandling(adminApi.getDownloadUrlForMultipleFiles);
const getDownloadUrlForMultipleEntityFiles = withErrorHandling(adminApi.getDownloadUrlForMultipleEntityFiles);

const FilesView = ({
  viewName,
  tagId,
  viewTitle,
  columnDefinitions,
  hasPermissionsToManage,
  uploadParams,
  entityInfo,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [filesState, setFilesState] = useState(getInitialFilesState);
  const [dialogState, setDialogState] = useState<DialogState>({});
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const filterPayload = useMemo(
    () => ({ tagId, viewName, searchTerm: filesState.searchTerm || undefined }),
    [filesState.searchTerm, tagId, viewName]
  );

  const fetchFiles = useCallback(
    (searchReq: SearchFilesRequest) => {
      return entityInfo
        ? adminApi.getEntityFiles(entityInfo.entityType, entityInfo.entityId, entityInfo.objectType, searchReq)
        : adminApi.getCompanyFiles(searchReq);
    },
    [entityInfo]
  );

  const { gridApiRef, dataSource, totalRows, isLoading, refreshDataSource } = useGridDataSourceWithRange(
    fetchFiles,
    filterPayload
  );

  const selectedFiles = useMemo(
    () => getRowsByIds<FilesRowModel>(gridApiRef, selectedRowIds),
    [gridApiRef, selectedRowIds]
  );

  const handleSearch = (value: string) => {
    const searchTerm = value.trim();
    if (searchTerm.length === 0 || searchTerm.length > 2) {
      setFilesState(searchFilesAction(searchTerm));
    }
  };

  const handleDownloadFile = async ({ catalogueId }: FileInfo) => {
    const [fileDownloadInfo, error] = entityInfo
      ? await getEntityFileDownloadInfo(entityInfo.entityType, entityInfo.entityId, entityInfo.objectType, catalogueId)
      : await getFileDownloadInfo(catalogueId);

    if (error) {
      sendNotificationError("Could not download the file");
      logError(error, "FilesView");
      return;
    }

    await downloadFileFromUrl(fileDownloadInfo.downloadUrl);
  };

  const handleDownloadMultipleFiles = async (catalogueIds: string[]) => {
    const [downloadUrl, error] = entityInfo
      ? await getDownloadUrlForMultipleEntityFiles(
          entityInfo.entityType,
          entityInfo.entityId,
          entityInfo.objectType,
          catalogueIds,
          `${entityInfo.entityName}_files.zip`
        )
      : await getDownloadUrlForMultipleFiles(catalogueIds, "files.zip");

    if (error || !downloadUrl) {
      sendNotificationError("Could not download multiple files as zip");
      logError(error, "FilesView");
      return;
    }

    await downloadFileFromUrl(downloadUrl);
  };

  const handlePreviewFile = async ({ catalogueId }: FileInfo) => {
    const [fileDownloadInfo, error] = entityInfo
      ? await getEntityFileDownloadInfo(entityInfo.entityType, entityInfo.entityId, entityInfo.objectType, catalogueId)
      : await getFileDownloadInfo(catalogueId);

    if (error) {
      sendNotificationError("Could not preview the file");
      logError(error, "FilesView");
      return;
    }

    setDialogState({ openDialog: "preview", previewFileInfo: fileDownloadInfo });
  };

  const handleDeleteFile = (fileInfo: FileInfo) => {
    setDialogState({
      openDialog: "delete",
      deletingFileIds: [fileInfo.catalogueId],
      deletingFilesDescription: fileInfo.fileName,
    });
  };

  const handleDeleteMultipleFiles = (catalogueIds: string[]) => {
    setDialogState({
      openDialog: "delete",
      deletingFileIds: catalogueIds,
      deletingFilesDescription: `${catalogueIds.length} files`,
    });
  };

  const handleFilesDeleted = () => {
    setDialogState({});
    refreshDataSource();
  };

  const uploadFile = async (uploadingFile: UploadingFile) => {
    if (!uploadingFile.file || !uploadParams) {
      return;
    }

    const { uploadFileApiCall } = uploadParams;
    const [fileInfo, uploadError] = await withErrorHandling(uploadFileApiCall)(uploadingFile.file);
    const uploadState = fileInfo ? { status: "completed" as const } : { status: "error" as const, uploadError };
    setFilesState(updateFileUploadStateAction(uploadingFile.id, uploadState));
    await wait(1000);
  };

  const handleAddFilesToUpload = async (files: File[]) => {
    if (!uploadParams) {
      return;
    }

    const { maxFileSize, acceptedFileExtensions } = uploadParams;

    const validateFile = fileToUploadValidator({
      maxFileSize,
      acceptedFileExtensions,
    });

    const uploadingFiles = getUploadingFiles(files, validateFile);
    setFilesState(startUploadingFilesAction(uploadingFiles.map(({ rowModel }) => rowModel)));

    await Promise.all(uploadingFiles.map(uploadFile));

    setFilesState(clearCompletedUploadsAction());
    refreshDataSource();
  };

  return (
    <FilesContextProvider
      filesState={filesState}
      setFilesState={setFilesState}
      gridApiRef={gridApiRef}
      hasPermissionsToManage={hasPermissionsToManage}
      entityInfo={entityInfo}
      onDownloadFile={handleDownloadFile}
      onDownloadMultipleFiles={handleDownloadMultipleFiles}
      onPreviewFile={handlePreviewFile}
      onDeleteFile={handleDeleteFile}
      onDeleteMultipleFiles={handleDeleteMultipleFiles}
    >
      <Stack sx={{ width: "100%" }}>
        <Box sx={{ py: 2, px: 3 }}>
          <Typography variant="subtitle1">{viewTitle}</Typography>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
          <Stack spacing={2.5} sx={{ width: "100%", py: 2.5, flex: 1 }}>
            {hasPermissionsToManage && uploadParams && (
              <Box sx={{ px: 3 }}>
                <FilesDropArea
                  allowMultiple
                  acceptedFileExtensions={uploadParams.acceptedFileExtensions}
                  subtitle={uploadParams.dropAreaSubtitle}
                  onFilesAdd={handleAddFilesToUpload}
                />
              </Box>
            )}
            <Stack spacing={2} direction="row" sx={{ px: 3, alignItems: "center", justifyContent: "space-between" }}>
              <RecordCounter records={totalRows ?? 0} hide={isLoading} />
              <SearchField debounceTimeMs={300} onSearch={handleSearch} />
            </Stack>
            <FilesGrid
              columnDefinitions={columnDefinitions}
              dataSource={dataSource}
              selectedRowIds={selectedRowIds}
              setSelectedRowIds={setSelectedRowIds}
            />
          </Stack>
          <Divider flexItem orientation="vertical" />
          <FileDetailsPanel files={selectedFiles} />
        </Box>
      </Stack>
      {dialogState.openDialog === "preview" && dialogState.previewFileInfo && (
        <PreviewFileDialog
          url={dialogState.previewFileInfo.downloadUrl}
          fileName={dialogState.previewFileInfo.fileName}
          onClose={() => setDialogState({})}
        />
      )}
      <DeleteFilesDialog
        catalogueIds={dialogState.deletingFileIds ?? []}
        description={dialogState.deletingFilesDescription ?? ""}
        open={dialogState.openDialog === "delete"}
        onCancel={() => setDialogState({})}
        onSubmit={handleFilesDeleted}
      />
    </FilesContextProvider>
  );
};

export default FilesView;
